import React, { useEffect, useState } from 'react';
import { executionOperationsService } from '../../services/executionOperationsService';

export const OperationsWorkerPlanePage: React.FC = () => {
  const [pools, setPools] = useState<unknown[]>([]);
  const [workers, setWorkers] = useState<unknown[]>([]);
  const [health, setHealth] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    void (async () => {
      const [p, w, h] = await Promise.all([
        executionOperationsService.getWorkerPools(),
        executionOperationsService.getWorkers(),
        executionOperationsService.getWorkersHealth(),
      ]);
      setPools(p.data || []);
      setWorkers(w.data || []);
      setHealth(h.data || null);
    })();
  }, []);

  return (
    <div className="space-y-4">
      {health && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Metric label="Total workers" value={health.workers_total} />
          <Metric label="Healthy" value={health.workers_healthy} />
          <Metric label="Unhealthy" value={health.workers_unhealthy} />
        </div>
      )}
      <Section title="Worker pools">
        <DataTable
          rows={pools as Record<string, unknown>[]}
          columns={['pool_key', 'display_name', 'worker_class', 'target_concurrency', 'drain_mode']}
        />
      </Section>
      <Section title="Worker instances">
        <DataTable
          rows={workers as Record<string, unknown>[]}
          columns={['worker_key', 'hostname', 'state', 'worker_class', 'process_type']}
        />
      </Section>
    </div>
  );
};

const Metric: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <div className="rounded-xl border border-enterprise-200 bg-white p-4">
    <div className="text-xs uppercase text-enterprise-500">{label}</div>
    <div className="mt-1 text-xl font-bold">{String(value ?? '—')}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
    <h2 className="mb-3 text-lg font-semibold text-enterprise-900">{title}</h2>
    {children}
  </div>
);

const DataTable: React.FC<{ rows: Record<string, unknown>[]; columns: string[] }> = ({ rows, columns }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-enterprise-100 text-left text-enterprise-500">
          {columns.map((c) => (
            <th key={c} className="px-2 py-2 font-medium">{c.replace(/_/g, ' ')}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-enterprise-50">
            {columns.map((c) => (
              <td key={c} className="px-2 py-2 font-mono text-xs">{String(row[c] ?? '—')}</td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="px-2 py-6 text-center text-enterprise-500">No data</td></tr>
        )}
      </tbody>
    </table>
  </div>
);
